import React, { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { 
  User, Settings, LogOut, HelpCircle, BookOpen, Bell, Moon, Sun, ChevronRight, Sparkles
} from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';

interface EnhancedUserMenuProps {
  user: any;
  signOut: () => void;
}

export const EnhancedUserMenu: React.FC<EnhancedUserMenuProps> = ({ user, signOut }) => {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const { colorScheme, updateColorScheme } = useTheme();
  const { t } = useLanguage();
  const isDarkMode = colorScheme === 'dark';

  const toggleDarkMode = () => {
    updateColorScheme(isDarkMode ? 'light' : 'dark');
  };

  const handleSignOut = () => {
    signOut();
    setIsOpen(false);
  };

  const getInitials = (email: string) => {
    const parts = email.split('@')[0].split(/[._-]/);
    if (parts.length > 1) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return email.substring(0, 2).toUpperCase();
  };

  const menuItems = [
    { icon: User, label: t('userMenu.myProfile'), path: '/settings?tab=profile', description: t('userMenu.viewEditProfile') },
    { icon: Settings, label: t('userMenu.settings'), path: '/settings', description: t('userMenu.accountPreferences') },
    { icon: Bell, label: t('userMenu.notifications'), path: '/settings?tab=notifications', description: t('userMenu.manageAlerts') },
  ];

  const helpItems = [
    { icon: BookOpen, label: t('userMenu.userManual'), path: '/user-manual', description: t('userMenu.completeGuide') },
    { icon: HelpCircle, label: t('userMenu.help'), path: '/contact', description: t('userMenu.supportFaq') },
  ];

  return (
    <div className="relative" ref={menuRef}>
      <Button
        variant="ghost"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-2 py-1.5 h-auto rounded-xl hover:bg-muted/50 transition-all duration-200"
      >
        <Avatar className="h-8 w-8 border-2 border-primary/20">
          <AvatarImage src={user?.user_metadata?.avatar_url} />
          <AvatarFallback className="bg-gradient-to-br from-primary/20 to-primary/10 text-primary font-semibold text-sm">
            {getInitials(user?.email || 'U')}
          </AvatarFallback>
        </Avatar>
        <span className="hidden lg:block text-sm font-medium max-w-[120px] truncate">
          {user?.email?.split('@')[0]}
        </span>
      </Button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
          
          <div className="absolute right-0 mt-2 w-72 bg-background/95 backdrop-blur-xl rounded-2xl shadow-2xl ring-1 ring-border/50 z-50 overflow-hidden animate-fade-in">
            {/* User Info Header */}
            <div className="p-4 bg-gradient-to-br from-primary/5 via-primary/10 to-transparent border-b border-border/50">
              <div className="flex items-center gap-3">
                <Avatar className="h-12 w-12 border-2 border-primary/20">
                  <AvatarImage src={user?.user_metadata?.avatar_url} />
                  <AvatarFallback className="bg-gradient-to-br from-primary to-primary/80 text-primary-foreground font-bold">
                    {getInitials(user?.email || 'U')}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-foreground truncate">
                    {user?.user_metadata?.full_name || user?.email?.split('@')[0]}
                  </p>
                  <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                </div>
                <Sparkles className="h-4 w-4 text-primary animate-pulse" />
              </div>
            </div>

            <div className="p-2">
              {menuItems.map(({ icon: Icon, label, path, description }) => (
                <Link key={path} to={path}
                  className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm hover:bg-muted/50 transition-all duration-200 group"
                  onClick={() => setIsOpen(false)}
                >
                  <div className="p-2 rounded-lg bg-muted group-hover:bg-primary/10 transition-colors">
                    <Icon className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" />
                  </div>
                  <div className="flex-1">
                    <span className="font-medium text-foreground">{label}</span>
                    <p className="text-xs text-muted-foreground">{description}</p>
                  </div>
                  <ChevronRight className="h-4 w-4 text-muted-foreground/50 group-hover:text-primary transition-all group-hover:translate-x-0.5" />
                </Link>
              ))}
            </div>

            <div className="px-2 pb-2">
              <button onClick={toggleDarkMode}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm hover:bg-muted/50 transition-all duration-200 group"
              >
                <div className="p-2 rounded-lg bg-muted group-hover:bg-primary/10 transition-colors">
                  {isDarkMode ? <Sun className="h-4 w-4 text-amber-500" /> : <Moon className="h-4 w-4 text-muted-foreground" />}
                </div>
                <div className="flex-1 text-left">
                  <span className="font-medium text-foreground">{t('userMenu.theme')}</span>
                  <p className="text-xs text-muted-foreground">
                    {isDarkMode ? t('userMenu.darkMode') : t('userMenu.lightMode')}
                  </p>
                </div>
                <div className={`w-10 h-6 rounded-full p-1 transition-colors ${isDarkMode ? 'bg-primary' : 'bg-muted'}`}>
                  <div className={`w-4 h-4 rounded-full bg-background shadow-sm transition-transform ${isDarkMode ? 'translate-x-4' : 'translate-x-0'}`} />
                </div>
              </button>
            </div>

            <div className="border-t border-border/50 p-2">
              {helpItems.map(({ icon: Icon, label, path }) => (
                <Link key={path} to={path}
                  className="flex items-center gap-3 px-3 py-2 rounded-xl text-sm hover:bg-muted/50 transition-all duration-200 group"
                  onClick={() => setIsOpen(false)}
                >
                  <Icon className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" />
                  <span className="text-muted-foreground group-hover:text-foreground">{label}</span>
                </Link>
              ))}
            </div>

            <div className="border-t border-border/50 p-2">
              <button onClick={handleSignOut}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-destructive hover:bg-destructive/10 transition-all duration-200"
              >
                <LogOut className="h-4 w-4" />
                <span className="font-medium">{t('userMenu.signOut')}</span>
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
